import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAo3Site } from "@/hooks/ao3SiteContext";
import { ao3Platforms } from "@/mocks/ao3Home";

export default function Ao3DownloadModal() {
  const { t } = useTranslation();
  const { config, downloadOpen, closeDownload } = useAo3Site();

  useEffect(() => {
    if (!downloadOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDownload();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [downloadOpen, closeDownload]);

  if (!downloadOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground-950/60 backdrop-blur-sm animate-ao3-fade-in"
      onClick={closeDownload}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("modal.title")}
        className="w-full max-w-lg rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6 animate-ao3-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">{t("modal.title")}</h3>
            <p className="mt-1 text-sm text-foreground-600">{t("modal.subtitle")}</p>
          </div>
          <button
            type="button"
            onClick={closeDownload}
            aria-label={t("modal.close")}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-700 transition-colors hover:bg-background-200/70"
          >
            <i className="ri-close-line text-lg leading-none" />
          </button>
        </div>

        <div className="mt-5 space-y-2.5">
          {ao3Platforms.map((platform) => {
            const href = config.downloads[platform.key as keyof typeof config.downloads] || "#";
            return (
              <a
                key={platform.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex cursor-pointer items-center gap-3 rounded-md border border-background-200/80 bg-background-100/70 px-3.5 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700 dark:text-primary-400">
                  <i className={`${platform.icon} text-xl leading-none`} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground-950">{t(`platforms.${platform.id}.desc`)}</span>
                  <span className="block text-xs text-foreground-600">{platform.id === "harmonyos" ? "HarmonyOS" : platform.id === "ios" ? "iOS" : platform.id === "android" ? "Android" : "Windows"}</span>
                </span>
                <span className="flex items-center gap-1 text-xs font-medium whitespace-nowrap text-primary-700 dark:text-primary-400">
                  {t("platforms.download")}
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}